import { useNavigate } from "react-router-dom";
import type { Role } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";

interface ReportCardDef {
  key: string;
  title: string;
  description: string;
  icon: string;
  iconClass: string;
  path: string;
}

const REPORTS: ReportCardDef[] = [
  {
    key: "statements",
    title: "Member statements",
    description: "Group-wise or consolidated statement — the document members ask for when they want to know where they stand.",
    icon: "ri-file-user-line",
    iconClass: "bg-primary-100 text-primary-700",
    path: "/reports/statements",
  },
  {
    key: "ledger",
    title: "Raw ledger",
    description: "The full chronological audit trail for a member — every entry, running balance and source reference.",
    icon: "ri-list-unordered",
    iconClass: "bg-accent-100 text-accent-700",
    path: "/reports/ledger",
  },
  {
    key: "collection-register",
    title: "Collection register",
    description: "Every receipt in a date range with totals by payment mode — reconcile cash and digital receipts.",
    icon: "ri-wallet-3-line",
    iconClass: "bg-success-100 text-success-700",
    path: "/reports/collection-register",
  },
  {
    key: "outstanding",
    title: "Outstanding & defaulters",
    description: "All unpaid demands aged into buckets so the organizer sees exactly where to focus recovery.",
    icon: "ri-alarm-warning-line",
    iconClass: "bg-danger-100 text-danger-700",
    path: "/reports/outstanding",
  },
  {
    key: "group-performance",
    title: "Group performance",
    description: "One group in full detail — every cycle, collection performance and member standing.",
    icon: "ri-bar-chart-grouped-line",
    iconClass: "bg-primary-100 text-primary-700",
    path: "/reports/group-performance",
  },
  {
    key: "auction-history",
    title: "Auction history",
    description: "All auctions across groups — winners, bids, discounts and dividends with averages.",
    icon: "ri-hammer-line",
    iconClass: "bg-accent-100 text-accent-700",
    path: "/reports/auction-history",
  },
  {
    key: "payout-register",
    title: "Payout register",
    description: "All payouts across groups with a totals row — vouchers, prizes, dues adjusted and net paid.",
    icon: "ri-bank-line",
    iconClass: "bg-success-100 text-success-700",
    path: "/reports/payout-register",
  },
  {
    key: "earnings",
    title: "Organizer earnings",
    description: "What the organizer has actually earned — reserved full-pool cycles and any commission.",
    icon: "ri-money-rupee-circle-line",
    iconClass: "bg-warning-100 text-warning-700",
    path: "/reports/earnings",
  },
];

/** Collection Agents see only collection and outstanding reports. Everyone else sees all. */
const REPORT_VISIBILITY: Record<Role, string[]> = {
  super_admin: REPORTS.map((r) => r.key),
  admin: REPORTS.map((r) => r.key),
  accountant: REPORTS.map((r) => r.key),
  viewer: REPORTS.map((r) => r.key),
  collection_agent: ["collection-register", "outstanding"],
};

export default function ReportsPage() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const visible = REPORT_VISIBILITY[role] ?? [];
  const cards = REPORTS.filter((r) => visible.includes(r.key));

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="Ledger statements and operational reports. Everything reconciles against the recorded data — no report modifies anything."
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map((r) => (
          <Card
            key={r.key}
            className="cursor-pointer transition-colors hover:border-primary-300 hover:bg-background-100/50"
          >
            <button type="button" onClick={() => navigate(r.path)} className="flex w-full cursor-pointer items-start gap-3 text-left">
              <div className={`flex h-10 w-10 flex-none items-center justify-center rounded-md ${r.iconClass}`}>
                <i className={`${r.icon} text-lg`} />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-foreground-900">{r.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">{r.description}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                  Open report <i className="ri-arrow-right-line" />
                </span>
              </div>
            </button>
          </Card>
        ))}
      </div>
    </div>
  );
}